import React, { useEffect } from 'react';
import { Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
import ScrollManager from './components/ScrollManager';
import Header from './components/Header';
import InnerHeader from './components/InnerHeader';
import ProjectCard from './components/ProjectCard';
import ProjectDetail from './components/ProjectDetail';
import Resume from './components/Resume';
import { ChevronLeft } from 'lucide-react';
import { portfolioData } from './data/portfolioData';
import './styles/Portfolio.css';
import ReactGA from 'react-ga4';

// ✅ Initialize GA4
ReactGA.initialize('G-P9LNPBKM3R');

// ✅ Hook to track page views on route change
const useAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
  }, [location]);

  // ✅ Global listener for outbound link clicks
  useEffect(() => {
    const handleOutboundClick = (e) => {
      const link = e.target.closest('a'); // find the nearest <a>
      if (link && link.href && link.target === '_blank') {
        const isExternal = link.hostname !== window.location.hostname;
        if (isExternal) {
          ReactGA.event({
            category: 'Outbound Link',
            action: 'Click',
            label: link.href,
          });
        }
      }
    };
    document.addEventListener('click', handleOutboundClick);
    return () => document.removeEventListener('click', handleOutboundClick);
  }, []);
};

// Home component
const Home = () => {
  const navigate = useNavigate();

  const handleProjectClick = (project) => {
    // ✅ Track click event
    ReactGA.event({
      category: 'Navigation',
      action: 'Open Project',
      label: project.title,
    });
    navigate(`/project/${project.id}`);
  };

  const handleResumeClick = () => {
    // ✅ Track click event
    ReactGA.event({
      category: 'Navigation',
      action: 'Open Resume',
    });
    navigate('/resume');
  };

  return (
    <div className="portfolio">
      <Header />
      <hr></hr>

      <main>
        <div className="projects-grid">
          <ProjectCard
            project={{
              title: "Resume",
              subtitle: "Relevant Work Experience, Web3, Education, Software & Skills"
            }}
            onClick={handleResumeClick}
          />

          {portfolioData.projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onClick={() => handleProjectClick(project)}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

// Project Detail component wrapper
const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const project = portfolioData.projects.find(p => p.id === projectId);

  if (!project) {
    return (
      <div className="portfolio">
        <InnerHeader />
        <div className="hr-back">
          <button className="back-button" onClick={() => navigate('/')}>
            <ChevronLeft size={32} />
          </button>
          <hr></hr>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Project not found</h2>
          <p>The project you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="portfolio">
      <InnerHeader />
      <div className="hr-back">
        <button className="back-button" onClick={() => navigate('/')}>
          <ChevronLeft size={32} />
        </button>
        <hr></hr>
      </div>
      <ProjectDetail project={project} />
    </div>
  );
};

// Resume component wrapper
const ResumePage = () => {
  const navigate = useNavigate();

  return (
    <div className="portfolio">
      <InnerHeader />
      <div className="hr-back">
        <button className="back-button" onClick={() => navigate('/')}>
          <ChevronLeft size={32} />
        </button>
        <hr></hr>
      </div>
      <Resume />
    </div>
  );
};

const App = () => {
  useAnalytics(); // ✅ Track page views automatically

  return (
    <>
      <ScrollManager />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/resume" element={<ResumePage />} />
        <Route path="/project/:projectId" element={<ProjectDetailPage />} />
      </Routes>
    </>
  );
};

export default App;